import { useState } from "react";
import { MondayStyledDropdownPicker } from "frontend/ui-components/picker/monday-styled-dropdown-picker/monday-styled-dropdown-picker";
import { FilterItemTrashIcon } from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/icons/filter-item-trash-icon";
import {
  LiveIntegrationColumn,
  LiveIntegrationFilter,
} from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/utils";
import { getMaxByLength } from "frontend/utils/array-utils";
import PickerOption from "frontend/cim-sub-menu/components/picker-option/picker-option";
import { EMPTY_DEFAULT_COLUMN, DropdownLabel } from "./types";
import styles from "./filter-item.module.css";

interface FilterItemProps {
  columns: LiveIntegrationColumn[];
  column?: LiveIntegrationColumn;
  values: string[];
  onRemove: () => void;
  onChange: (filter: LiveIntegrationFilter) => void;
}

const CheckboxSelected = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
      <path
        d="M0.5 2.72222L2.51357 4.87962C2.54629 4.91468 2.60186 4.91468 2.63458 4.87962L6.72222 0.5"
        stroke="white"
        strokeLinecap="round"
      />
    </svg>
  );
};

function parseColumnSettings(column: LiveIntegrationColumn | null) {
  let labels: Record<string, string> = {};
  let colors: Record<string, { color: string; border: string; var_name: string }> = {};
  if (!column) return { labels, colors };

  const settings = column.settings;
  if (column.type === "status") {
    labels = settings?.labels ?? {};
    colors = settings?.labels_colors ?? {};
    // Monday adds a special value for "unset" - labeled as 5
    if (column.id === "status") {
      labels["5"] ??= "";
      colors["5"] ??= { color: "#c4c4c4", border: "#b0b0b0", var_name: "grey" };
    }
  } else if (column.type === "dropdown") {
    const rawLabels: Array<DropdownLabel> = settings?.labels ?? [];
    for (const { id, name } of rawLabels) {
      labels[id] = name;
    }
  }

  return { labels, colors };
}

export default function FilterItem({ columns = [], column, values = [], onRemove, onChange }: FilterItemProps) {
  const [selectedColumn, setSelectedColumn] = useState<LiveIntegrationColumn | null>(column || null);
  const [selectedValues, setSelectedValues] = useState<string[]>(values);

  const { labels, colors } = parseColumnSettings(selectedColumn);

  const statusIds = Object.keys(labels);

  const handleColumnChange = (col: LiveIntegrationColumn | null) => {
    setSelectedColumn(col);
    setSelectedValues([]);
    if (col && col.id !== EMPTY_DEFAULT_COLUMN.id) {
      onChange({
        columnId: col.id,
        columnValues: [],
      });
    }
  };

  const handleValuesChange = (newValues: string[]) => {
    setSelectedValues(newValues);
    if (selectedColumn && selectedColumn.id !== EMPTY_DEFAULT_COLUMN.id) {
      onChange({
        columnId: selectedColumn.id,
        columnValues: newValues,
      });
    }
  };

  const selectedItem = getMaxByLength(
    statusIds
      .map((labelId) => ({
        id: labelId,
        title: labels[labelId],
        color: colors[labelId]?.color,
      }))
      .filter((item) => selectedValues.includes(item.title)),
    "title"
  );

  const isValidColumn = selectedColumn && selectedColumn.id !== EMPTY_DEFAULT_COLUMN.id && statusIds.length > 0;

  return (
    <div className={styles.filterItem}>
      <span className={styles.filterText}>Where</span>

      <MondayStyledDropdownPicker<LiveIntegrationColumn>
        options={columns}
        selectedOption={selectedColumn}
        onChange={handleColumnChange}
        isSelected={(col) => col.id === selectedColumn?.id}
        getTitle={(col) => col.title}
        renderOption={(option) => (
          <PickerOption>
            <span className={styles.optionText}>{option?.title || ""}</span>
          </PickerOption>
        )}
        renderSelectedOption={(option) => (
          <PickerOption>
            <span className={styles.selectedText}>{option?.title || "Column"}</span>
          </PickerOption>
        )}
        placeholder="Column"
        width="119px"
        optionsWidth="158px"
        useFloatingDropdown={true}
        enableSearch={true}
        trackingEventName="cim_column_selected"
      />

      <span className={styles.filterText}>is</span>

      <MondayStyledDropdownPicker<string>
        options={statusIds}
        selectedOption={selectedItem?.id || null}
        onChange={(statusId) => {
          const label = labels[statusId];
          // Toggle selection for multi-select
          const newValues = selectedValues.includes(label)
            ? selectedValues.filter((v) => v !== label)
            : [...selectedValues, label];
          handleValuesChange(newValues);
        }}
        isSelected={(id) => {
          const label = labels[id];
          return selectedValues.includes(label);
        }}
        getTitle={(id) => labels[id]}
        renderOption={(option) => {
          const title = labels[option];
          const color = colors[option]?.color;
          const isSelected = selectedValues.includes(title);
          return (
            <PickerOption data-selected={isSelected}>
              <div className={styles.checkbox} data-checked={isSelected}>
                {isSelected && <CheckboxSelected />}
              </div>
              {color && <span className={styles.statusDot} style={{ backgroundColor: color }} />}
              <span className={styles.optionText}>{title}</span>
            </PickerOption>
          );
        }}
        renderSelectedOption={() => {
          if (!selectedItem || selectedValues.length === 0) {
            return <div className={styles.selectedValue}>Value</div>;
          }
          return (
            <div className={styles.selectedValue}>
              {selectedItem.color && (
                <span className={styles.statusDot} style={{ backgroundColor: selectedItem.color }} />
              )}
              <span className={styles.selectedText}>{selectedItem.title}</span>
              {selectedValues.length > 1 && <span className={styles.counter}>+{selectedValues.length - 1}</span>}
            </div>
          );
        }}
        placeholder="Value"
        width="119px"
        optionsWidth="158px"
        useFloatingDropdown={true}
        enableSearch={true}
        disabled={!isValidColumn}
        trackingEventName="cim_column_value_selected"
      />

      <button
        className={styles.trashButton}
        onClick={onRemove}
        data-active={selectedValues.length > 0}
        aria-label="Remove filter"
      >
        <FilterItemTrashIcon color="#1E1E1E" />
      </button>
    </div>
  );
}
